import { Button, Input, Label, Skeleton, Spinner, Switch } from '@repo/ui'
import { createFileRoute } from '@tanstack/react-router'
import { Save } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import { useAuth } from '@/hooks/use-auth'
import { useCadence, useUpdateCadence } from '@/hooks/use-cadences'
import { useTags } from '@/hooks/use-leads'

export const Route = createFileRoute('/_app/cadencias/$cadenceId/agenda')({
  component: CadenceSchedulePage,
})

const DAYS = [
  { value: 1, label: 'Seg' },
  { value: 2, label: 'Ter' },
  { value: 3, label: 'Qua' },
  { value: 4, label: 'Qui' },
  { value: 5, label: 'Sex' },
  { value: 6, label: 'Sab' },
  { value: 0, label: 'Dom' },
]

const STOP_STAGE_OPTIONS = [
  { value: 'Novo', label: 'Novo' },
  { value: 'Contactado', label: 'Contactado' },
  { value: 'Qualificado', label: 'Qualificado' },
  { value: 'Proposta', label: 'Proposta' },
  { value: 'Ganho', label: 'Ganho' },
  { value: 'Perdido', label: 'Perdido' },
  { value: 'Fora de ICP', label: 'Fora de ICP' },
]

function CadenceSchedulePage() {
  const { cadenceId } = Route.useParams()
  const { currentOrgId } = useAuth()
  const { data: cadence, isLoading } = useCadence(currentOrgId, cadenceId)
  const updateCadence = useUpdateCadence(currentOrgId, cadenceId)
  const [days, setDays] = useState<number[]>([1, 2, 3, 4, 5])
  const [start, setStart] = useState('08:00')
  const [end, setEnd] = useState('18:00')
  const [retryOnFrequencyCap, setRetryOnFrequencyCap] = useState(false)
  const [frequencyCapHours, setFrequencyCapHours] = useState('6')
  const [stopOnReply, setStopOnReply] = useState(true)
  const [aiHandlesReplies, setAiHandlesReplies] = useState(true)
  const [stopOnAppointment, setStopOnAppointment] = useState(false)
  const [stopStages, setStopStages] = useState<string[]>([])
  const [onReplyTagIds, setOnReplyTagIds] = useState<string[]>([])
  const { data: tags = [] } = useTags(currentOrgId)

  useEffect(() => {
    if (!cadence) return
    setRetryOnFrequencyCap(cadence.frequencyCapRetryHours != null)
    if (cadence.frequencyCapRetryHours != null) {
      setFrequencyCapHours(String(cadence.frequencyCapRetryHours))
    }
    setStopOnReply(cadence.stop_on_reply)
    setAiHandlesReplies(cadence.replyHandling !== 'HUMAN_QUEUE')
    if (cadence.delivery_days.length > 0) setDays(cadence.delivery_days)
    if (cadence.delivery_start_time) setStart(cadence.delivery_start_time)
    if (cadence.delivery_end_time) setEnd(cadence.delivery_end_time)
    setStopOnAppointment(cadence.stop_on_appointment)
    setOnReplyTagIds(cadence.on_reply_tag_ids ?? [])
    setStopStages(
      cadence.stop_stages.flatMap((stage) => {
        if (stage === 'QUALIFIED') return ['Qualificado']
        if (stage === 'CUSTOMER') return ['Ganho']
        if (stage === 'INACTIVE') return ['Perdido', 'Fora de ICP']
        return [stage]
      })
    )
  }, [cadence])

  function toggleDay(day: number) {
    setDays((current) =>
      current.includes(day)
        ? current.filter((item) => item !== day)
        : [...current, day].sort()
    )
  }

  function toggleStopStage(stage: string) {
    setStopStages((current) =>
      current.includes(stage)
        ? current.filter((item) => item !== stage)
        : [...current, stage]
    )
  }

  function toggleOnReplyTag(tagId: string) {
    setOnReplyTagIds((current) =>
      current.includes(tagId)
        ? current.filter((item) => item !== tagId)
        : [...current, tagId]
    )
  }

  async function save() {
    if (days.length === 0) {
      toast.error('Selecione ao menos um dia')
      return
    }
    if (start >= end) {
      toast.error('Horário inicial precisa ser antes do final')
      return
    }
    await updateCadence.mutateAsync({
      frequencyCapRetryHours: retryOnFrequencyCap
        ? Math.min(72, Math.max(1, Number(frequencyCapHours) || 6))
        : null,
      stop_on_reply: stopOnReply,
      replyHandling: aiHandlesReplies ? 'AI_AGENT' : 'HUMAN_QUEUE',
      delivery_days: days,
      delivery_start_time: start,
      delivery_end_time: end,
      stop_on_appointment: stopOnAppointment,
      stop_stages: stopStages,
      on_reply_tag_ids: onReplyTagIds,
    })
    toast.success('Regras salvas')
  }

  if (isLoading || !cadence) {
    return <Skeleton className="h-80 w-full rounded-md" />
  }

  return (
    <div className="space-y-6">
      <section className="bg-card rounded-md border p-5">
        <h2 className="text-base font-semibold">Janela de envio</h2>
        <p className="text-muted-foreground mt-1 text-sm">
          Etapas só são enviadas dentro dos dias e horários selecionados. Fora
          da janela, a próxima etapa aguarda automaticamente.
        </p>
        <div className="mt-5 space-y-5">
          <div className="space-y-2">
            <Label className="text-xs">Dias permitidos</Label>
            <div className="flex flex-wrap gap-2">
              {DAYS.map((day) => {
                const active = days.includes(day.value)
                return (
                  <button
                    key={day.value}
                    type="button"
                    onClick={() => toggleDay(day.value)}
                    className={`rounded-md border px-3 py-1.5 text-xs transition-colors ${
                      active
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-input bg-background text-muted-foreground hover:bg-muted'
                    }`}
                  >
                    {day.label}
                  </button>
                )
              })}
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="schedule-start" className="text-xs">
                Início
              </Label>
              <Input
                id="schedule-start"
                type="time"
                value={start}
                onChange={(event) => setStart(event.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="schedule-end" className="text-xs">
                Fim
              </Label>
              <Input
                id="schedule-end"
                type="time"
                value={end}
                onChange={(event) => setEnd(event.target.value)}
              />
            </div>
          </div>
          <div className="rounded-md border p-3">
            <label className="flex items-center justify-between gap-4">
              <span>
                <span className="block text-sm font-medium">
                  Reenviar se a Meta bloquear por excesso de marketing
                </span>
                <span className="text-muted-foreground block text-sm">
                  Quando o número atinge o limite temporário de mensagens de
                  marketing da Meta, reagenda a etapa em vez de perder o envio.
                </span>
              </span>
              <Switch
                checked={retryOnFrequencyCap}
                onCheckedChange={setRetryOnFrequencyCap}
              />
            </label>
            {retryOnFrequencyCap ? (
              <div className="mt-3 flex items-center gap-2">
                <Label htmlFor="frequency-cap-hours" className="text-xs">
                  Reenviar após (horas)
                </Label>
                <Input
                  id="frequency-cap-hours"
                  type="number"
                  min={1}
                  max={72}
                  value={frequencyCapHours}
                  onChange={(event) => setFrequencyCapHours(event.target.value)}
                  className="w-24"
                />
              </div>
            ) : null}
          </div>
          <p className="text-muted-foreground text-xs">
            Fuso horário: <strong>{cadence.timezone}</strong>
          </p>
        </div>
      </section>

      <section className="bg-card rounded-md border p-5">
        <h2 className="text-base font-semibold">Regras de bloqueio</h2>
        <p className="text-muted-foreground mt-1 text-sm">
          Condições que encerram a cadência para um lead específico. O motivo
          fica registrado no histórico.
        </p>
        <div className="mt-4 space-y-3">
          <label className="flex items-center justify-between gap-4 rounded-md border p-3">
            <span>
              <span className="block text-sm font-medium">
                Parar quando o lead responder
              </span>
              <span className="text-muted-foreground block text-sm">
                Encerra a cadência automaticamente ao receber resposta.
              </span>
            </span>
            <Switch checked={stopOnReply} onCheckedChange={setStopOnReply} />
          </label>
          <label className="flex items-center justify-between gap-4 rounded-md border p-3">
            <span>
              <span className="block text-sm font-medium">
                IA responde quando o lead retornar
              </span>
              <span className="text-muted-foreground block text-sm">
                Mantém o agente da cadência mesmo após entrar em aguardando
                contato. Desligado, a resposta vai para a fila humana.
              </span>
            </span>
            <Switch
              checked={aiHandlesReplies}
              onCheckedChange={setAiHandlesReplies}
            />
          </label>
          <label className="flex items-center justify-between gap-4 rounded-md border p-3">
            <span>
              <span className="block text-sm font-medium">
                Parar ao criar agendamento
              </span>
              <span className="text-muted-foreground block text-sm">
                Encerra a cadência quando um agendamento for criado para o lead.
              </span>
            </span>
            <Switch
              checked={stopOnAppointment}
              onCheckedChange={setStopOnAppointment}
            />
          </label>
          <div className="space-y-2 rounded-md border p-3">
            <span className="block text-sm font-medium">
              Parar ao mudar status do lead
            </span>
            <span className="text-muted-foreground block text-sm">
              Encerra a cadência quando o lead atingir um dos status abaixo.
            </span>
            <div className="mt-2 flex flex-wrap gap-2">
              {STOP_STAGE_OPTIONS.map((stage) => (
                <Button
                  key={stage.value}
                  type="button"
                  variant={
                    stopStages.includes(stage.value) ? 'secondary' : 'outline'
                  }
                  size="sm"
                  onClick={() => toggleStopStage(stage.value)}
                >
                  {stage.label}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-card rounded-md border p-5">
        <h2 className="text-base font-semibold">Ao receber resposta</h2>
        <p className="text-muted-foreground mt-1 text-sm">
          Ações disparadas na primeira resposta do lead, logo depois que a
          cadência é encerrada. Dependem de{' '}
          <strong>Parar quando o lead responder</strong> estar ligado.
        </p>
        {!stopOnReply ? (
          <p className="text-muted-foreground mt-3 rounded-md border border-dashed p-3 text-sm">
            Ligue “Parar quando o lead responder” acima para configurar as
            ações.
          </p>
        ) : (
          <div className="mt-4 space-y-2 rounded-md border p-3">
            <span className="block text-sm font-medium">Aplicar tags</span>
            <span className="text-muted-foreground block text-sm">
              A tag é aplicada como qualquer outra — se for tag de entrada de
              outra cadência, o lead entra nela automaticamente.
            </span>
            <div className="mt-2 flex flex-wrap gap-2">
              {tags.length === 0 ? (
                <span className="text-muted-foreground text-xs">
                  Nenhuma tag criada ainda.
                </span>
              ) : (
                tags.map((tag) => {
                  const selected = onReplyTagIds.includes(tag.id)
                  return (
                    <button
                      key={tag.id}
                      type="button"
                      onClick={() => toggleOnReplyTag(tag.id)}
                      className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs transition-colors ${
                        selected
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'hover:bg-muted text-muted-foreground'
                      }`}
                    >
                      {tag.name}
                    </button>
                  )
                })
              )}
            </div>
          </div>
        )}
      </section>

      <div className="flex justify-end">
        <Button
          type="button"
          onClick={() => void save()}
          disabled={updateCadence.isPending}
        >
          {updateCadence.isPending ? <Spinner /> : <Save className="h-4 w-4" />}
          Salvar
        </Button>
      </div>
    </div>
  )
}
